import axios from 'axios';
import { throttle } from 'lodash';
import { stats } from '@fiverr-private/obs';
import { calcRetryAfterMilliSeconds, HTTP_CODES } from '../../Utils';
import { UploadErrorTypes } from '../../errors/UploadErrorTypes';
import RemoteActionError from '../../errors/RemoteActionError';

const THROLLTE_WAIT_LIMIT_IN_MS = 1000;
const MAX_WAIT_TIME_IN_MS = 2 * 1000;

export default class PendingAttachmentHandler {

    constructor(objectName, attachmentName, createPendingAttachmentIdUrl, s3ParsedData, file, retryAttempts) {
        this.objectName = objectName;
        this.attachmentName = attachmentName;
        this.createPendingAttachmentIdUrl = createPendingAttachmentIdUrl;
        this.s3ParsedData = s3ParsedData;
        this.file = file;
        this.retryAttempts = retryAttempts;
    }

    createPendingAttachment() {
        return axios.post(
            this.createPendingAttachmentIdUrl,
            {
                file_path: this.s3ParsedData.key,
                bucket_name: this.s3ParsedData.bucketName,
                attachment_name: this.attachmentName,
                file_data: {
                    file_path: this.s3ParsedData.key,
                    bucket_name: this.s3ParsedData.bucketName,
                    etag: this.s3ParsedData.etag,
                    attachment_name: this.attachmentName,
                    file_size: this.file.size,
                    content_type: this.file.type
                }
            }
        );
    }

    logCreatePendingAttachmentTime(startTime) {
        stats.time(
            `filerr_uploader.${this.objectName}.${this.attachmentName}`,
            'pending_attachment_creation',
            Date.now() - startTime
        );
    }

    handleCreatePendingAttachmentIdLogic() {
        if (!this.createPendingAttachmentIdUrl) {
            return this.s3ParsedData;
        }

        const startTime = Date.now();
        const retryCreatePendingAttachment = throttle(async(retryAttempts, resolve, reject) => {
            try {
                const response = await this.createPendingAttachment();
                this.logCreatePendingAttachmentTime(startTime);
                resolve(Object.assign(this.s3ParsedData, { pendingAttachmentData: response.data }));
            }
            catch (error) {
                if (retryAttempts) {
                    let waitTime = 0;

                    if (error?.response?.status === HTTP_CODES.TOO_MANY_REQUESTS) {
                        waitTime = Math.min(MAX_WAIT_TIME_IN_MS, calcRetryAfterMilliSeconds(error.response));
                    }

                    setTimeout(() => {
                        retryCreatePendingAttachment(retryAttempts - 1, resolve, reject);
                    }, waitTime);
                } else {
                    reject(new RemoteActionError(error.response?.status || 0, error.message, UploadErrorTypes.CREATE_PENDING_ATTACHMENT));
                }
            }
        }, THROLLTE_WAIT_LIMIT_IN_MS, { 'leading': true });

        return new Promise((resolve, reject) => {
            retryCreatePendingAttachment(this.retryAttempts, resolve, reject);
        });
    }
}
